import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  CBadge,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilSearch } from "@coreui/icons";
import { HELP_CATEGORIES } from "./categories";
import { helpArticlesByCategory } from "./help-content";
import type { HelpArticle } from "./help-content";
import { searchHelp } from "./search";
import { useHelpArticles } from "./useHelpArticles";
import { useSearchGapReporting } from "./useSearchGapReporting";
import { helpArticlePath, routePaths } from "../../routes/route-paths";

export function HelpIndex() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const helpArticles = useHelpArticles();

  const hits = useMemo(() => searchHelp(helpArticles, query), [helpArticles, query]);
  const searching = query.trim().length > 0;
  useSearchGapReporting(query, hits.length);

  const populatedCategories = HELP_CATEGORIES.filter(
    (category) => helpArticlesByCategory(helpArticles, category.id).length > 0
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
      if (event.key === "Escape" && document.activeElement === searchRef.current) {
        setQuery("");
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div>
      <h1 className="help-page-title mb-2">{t("help:index.title")}</h1>
      <p className="text-body-secondary mb-4">{t("help:index.subtitle")}</p>

      <CInputGroup className="mb-4">
        <CInputGroupText>
          <CIcon icon={cilSearch} />
        </CInputGroupText>
        <CFormInput
          ref={searchRef}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("help:index.searchPlaceholder")}
          aria-label={t("help:index.searchLabel")}
        />
      </CInputGroup>

      <p className="text-body-secondary small mb-4">
        {searching ? (
          <Trans
            i18nKey="index.resultCount"
            ns="help"
            count={hits.length}
            values={{ query: query.trim() }}
            components={{ 1: <strong className="text-body" /> }}
          />
        ) : (
          <Trans
            i18nKey="index.articleCount"
            ns="help"
            count={helpArticles.length}
            values={{ categories: populatedCategories.length }}
            components={{ 1: <strong className="text-body" /> }}
          />
        )}
      </p>

      {searching ? (
        hits.length > 0 ? (
          <CRow xs={{ gutter: 4 }}>
            {hits.map((hit) => (
              <CCol md={6} xl={4} key={hit.article.slug}>
                <ArticleCard
                  article={hit.article}
                  matchedKeyword={hit.matchedKeyword}
                />
              </CCol>
            ))}
          </CRow>
        ) : (
          <CCard>
            <CCardBody className="text-center py-5">
              <p className="mb-1 fw-semibold">
                {t("help:index.noResults", { query: query.trim() })}
              </p>
              <p className="text-body-secondary small mb-0">
                {t("help:index.noResultsHint")}{" "}
                <Link to={routePaths.contact}>
                  {t("help:index.contactSupport")}
                </Link>
              </p>
            </CCardBody>
          </CCard>
        )
      ) : (
        populatedCategories.map((category) => {
          const articles = helpArticlesByCategory(helpArticles, category.id);

          return (
            <section className="mb-4" key={category.id}>
              <div className="help-category-head mb-3">
                <h2 className="help-category-label mb-0">{category.label}</h2>
                <span className="font-monospace small text-body-secondary">
                  {articles.length}
                </span>
                <hr className="help-category-rule" />
              </div>

              <CRow xs={{ gutter: 4 }}>
                {articles.map((article) => (
                  <CCol md={6} xl={4} key={article.slug}>
                    <ArticleCard article={article} matchedKeyword={null} />
                  </CCol>
                ))}
              </CRow>
            </section>
          );
        })
      )}
    </div>
  );
}

function ArticleCard({
  article,
  matchedKeyword,
}: {
  article: HelpArticle;
  matchedKeyword: string | null;
}) {
  const { t } = useTranslation();

  return (
    <CCard className="h-100">
      <CCardBody className="d-flex flex-column">
        <h3 className="h6 mb-2">
          <Link
            to={helpArticlePath(article.slug)}
            className="stretched-link text-decoration-none"
          >
            {article.title}
          </Link>
        </h3>

        <p className="text-body-secondary small mb-3">{article.summary}</p>

        {matchedKeyword && (
          <div className="mb-2">
            <CBadge
              className="bg-success-subtle text-success-emphasis fw-normal"
              shape="rounded-pill"
            >
              {t("help:index.matchedKeyword", { keyword: matchedKeyword })}
            </CBadge>
          </div>
        )}

        <div className="mt-auto d-flex align-items-center gap-2">
          <span className="help-slug-chip font-monospace">/{article.slug}</span>
          <span className="font-monospace text-body-secondary help-card-meta">
            {t("help:article.readTimeShort", { count: article.readMinutes })}
          </span>
        </div>
      </CCardBody>
    </CCard>
  );
}
